
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Mail, ExternalLink } from "lucide-react"
import { cn } from "@/lib/utils"
import { Course } from "@/types/course"

interface SiteContentProps {
  fullName?: string
  bio?: string
  avatarUrl?: string | null
  bannerUrl?: string | null
  socialLinks?: Record<string, string> | null
  email?: string | null
  courses?: Course[]
}

export function SiteContent({
  fullName,
  bio,
  avatarUrl,
  bannerUrl,
  socialLinks,
  email,
  courses = []
}: SiteContentProps) {
  // Log the props we're receiving to verify data flow
  console.log("[SiteContent] Rendering with props:", { 
    fullName, 
    bio, 
    avatarUrl, 
    bannerUrl, 
    socialLinks: socialLinks ? Object.keys(socialLinks) : 'none',
    email,
    coursesCount: courses?.length || 0,
    courseDetails: courses?.map(c => ({ id: c.id, title: c.title, price: c.price }))
  });
  
  return (
    <div className="flex flex-col min-h-full bg-background">
      {/* Banner */}
      <div 
        className="h-48 w-full bg-cover bg-center relative"
        style={{ 
          backgroundImage: bannerUrl ? `url(${bannerUrl})` : 'linear-gradient(to right, #4F46E5, #7C3AED)'
        }}
      >
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
          <Avatar className="w-32 h-32 border-4 border-background">
            <AvatarImage src={avatarUrl || undefined} alt={fullName || "Profile"} />
            <AvatarFallback className="text-3xl bg-primary text-primary-foreground">
              {fullName ? fullName[0].toUpperCase() : '?'}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pt-20 pb-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Profile Info */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{fullName || 'Your Name'}</h1>
            {bio && <p className="text-muted-foreground">{bio}</p>}
          </div>

          {/* Contact Button */}
          {email && (
            <div className="flex justify-center">
              <Button className="gap-2">
                <Mail className="h-4 w-4" />
                Contact Me
              </Button>
            </div>
          )}

          {/* Social Links */}
          {socialLinks && Object.keys(socialLinks).length > 0 && (
            <div className="flex flex-wrap gap-3 justify-center">
              {Object.entries(socialLinks).map(([platform, url]) => (
                <a 
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-md",
                    "bg-muted hover:bg-muted/80 transition-colors",
                    "text-sm text-muted-foreground"
                  )}
                >
                  {platform}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          )}
          
          {/* Courses Section */}
          {courses && courses.length > 0 ? (
            <div className="mt-12 pt-6 border-t border-border">
              <h2 className="text-xl font-semibold mb-6 text-center">My Courses</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {courses.map((course) => (
                  <div key={course.id} className="rounded-lg overflow-hidden border border-border bg-card hover:shadow-md transition-all">
                    <div className="aspect-video bg-muted relative">
                      {course.thumbnail_url && (
                        <img 
                          src={course.thumbnail_url} 
                          alt={course.title} 
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium mb-1">{course.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{course.description}</p>
                      {course.price !== null && course.price > 0 ? (
                        <div className="mt-2">
                          <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded">
                            ${course.price}
                          </span>
                        </div>
                      ) : (
                        <div className="mt-2">
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                            Free
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-12 pt-6 border-t border-border text-center">
              <h2 className="text-xl font-semibold mb-3">My Courses</h2>
              <p className="text-muted-foreground">No courses available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
